/**
 * Vue composable for WebSocket functionality
 */

import { ref, onMounted, onUnmounted, watch } from 'vue';
import websocketService from '@/services/websocketService.js';
import { useAuthState } from '@/composables/authState.js';

export function useWebSocket() {
    const isConnected = ref(false);
    const connectionError = ref(null);
    const { status, AUTH_STATUSES } = useAuthState();

    const subscriptions = new Set();
    let stopStatusWatch = null;
    let listenersAttached = false;

    // Connection status handlers
    const handleConnected = () => {
        isConnected.value = true;
        connectionError.value = null;
    };

    const handleDisconnected = () => {
        isConnected.value = false;
    };

    const handleError = (error) => {
        connectionError.value = error;
        isConnected.value = false;
    };

    const handleUnauthorized = (payload = {}) => {
        const message = payload?.message || 'WebSocket unauthorized';
        const unauthorizedError = new Error(message);
        if (payload?.code) {
            unauthorizedError.code = payload.code;
        }
        connectionError.value = unauthorizedError;
        isConnected.value = false;
    };

    const attachBaseListeners = () => {
        if (listenersAttached) {
            return;
        }
        websocketService.on('ready', handleConnected);
        websocketService.on('checking', handleDisconnected);
        websocketService.on('disconnected', handleDisconnected);
        websocketService.on('error', handleError);
        websocketService.on('unauthorized', handleUnauthorized);
        listenersAttached = true;
    };

    const detachBaseListeners = () => {
        if (!listenersAttached) {
            return;
        }
        websocketService.off('ready', handleConnected);
        websocketService.off('checking', handleDisconnected);
        websocketService.off('disconnected', handleDisconnected);
        websocketService.off('error', handleError);
        websocketService.off('unauthorized', handleUnauthorized);
        listenersAttached = false;
    };

    const cleanupSubscriptions = () => {
        subscriptions.forEach(unsubscribe => {
            try {
                unsubscribe();
            } catch (error) {
                globalThis.clientLogger?.warn('Failed to unsubscribe from WebSocket event:', error);
            }
        });
        subscriptions.clear();
    };

    const handleAuthStatusChange = (newStatus, oldStatus) => {
        if (newStatus === oldStatus) {
            return;
        }

        if (newStatus === AUTH_STATUSES.AUTHENTICATED) {
            attachBaseListeners();
            websocketService.allowReconnect();
            if (!websocketService.isConnected()) {
                websocketService.connect();
            }
        } else if (newStatus === AUTH_STATUSES.LOGGED_OUT) {
            websocketService.disconnect();
            detachBaseListeners();
            cleanupSubscriptions();
            isConnected.value = false;
            connectionError.value = null;
        }
    };

    onMounted(() => {
        attachBaseListeners();

        if (status.value === AUTH_STATUSES.AUTHENTICATED) {
            websocketService.allowReconnect();
            websocketService.connect();
            isConnected.value = websocketService.ready;
        } else {
            globalThis.clientLogger?.info('WS connect skipped: user not authenticated');
            isConnected.value = false;
        }

        stopStatusWatch = watch(status, handleAuthStatusChange);
    });

    onUnmounted(() => {
        // Clean up listeners
        detachBaseListeners();
        cleanupSubscriptions();
        if (stopStatusWatch) {
            stopStatusWatch();
            stopStatusWatch = null;
        }
    });

    /**
     * Subscribe to specific event type
     */
    const subscribe = (eventType, callback) => {
        websocketService.on(eventType, callback);

        const unsubscribe = () => {
            websocketService.off(eventType, callback);
            subscriptions.delete(unsubscribe);
        };

        subscriptions.add(unsubscribe);

        // Return unsubscribe function
        return unsubscribe;
    };

    /**
     * Subscribe to order cancelled events for specific bot
     */
    const subscribeToOrderCancelled = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('order_cancelled', handler);
    };

    /**
     * Subscribe to order filled events for specific bot
     */
    const subscribeToOrderFilled = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('order_filled', handler);
    };

    /**
     * Subscribe to bot status events for specific bot
     */
    const subscribeToBotStatus = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('bot_status', handler);
    };

    /**
     * Subscribe to P&L updates for specific bot
     */
    const subscribeToPnLUpdate = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('pnl_update', handler);
    };

    /**
     * Subscribe to order created events for specific bot
     */
    const subscribeToOrderCreated = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('order_created', handler);
    };

    /**
     * Subscribe to position update events for specific bot (strategy bots)
     */
    const subscribeToPositionUpdate = (botId, callback) => {
        const handler = (data) => {
            if (data.bot_id === botId) {
                callback(data);
            }
        };
        
        return subscribe('position_updated', handler);
    };

    return {
        isConnected,
        connectionError,
        subscribe,
        subscribeToOrderCancelled,
        subscribeToOrderFilled,
        subscribeToBotStatus,
        subscribeToPnLUpdate,
        subscribeToOrderCreated,
        subscribeToPositionUpdate
    };
}
